/**
 * Minimum Booking Period utilities.
 *
 * Enforce minimum and maximum booking duration.
 * Useful for: vacation rentals (min 3 nights), hourly services (min 2 hours).
 *
 * Config in listing.publicData.bookingPeriod = {
 *   minDays: 2,   — minimum days (for day/night bookings)
 *   maxDays: 14,  — maximum days (optional)
 *   minHours: null, — for hourly bookings
 *   maxHours: null,
 * }
 */

export const DEFAULT_MIN_DAYS = 1;
export const DEFAULT_MAX_DAYS = 365;

/**
 * Get booking period config from listing.
 * @param {Object|null} listing
 * @returns {{ minDays, maxDays, minHours, maxHours }}
 */
export const getBookingPeriod = (listing) => {
  const p = listing?.attributes?.publicData?.bookingPeriod;
  return {
    minDays: p?.minDays ?? DEFAULT_MIN_DAYS,
    maxDays: p?.maxDays ?? DEFAULT_MAX_DAYS,
    minHours: p?.minHours ?? null,
    maxHours: p?.maxHours ?? null,
  };
};

/**
 * Check if a booking duration meets minimum requirements.
 * @param {number} days
 * @param {{ minDays, maxDays }} period
 * @returns {string|null} — error message or null
 */
export const validateBookingDuration = (days, period = {}) => {
  const { minDays = DEFAULT_MIN_DAYS, maxDays = DEFAULT_MAX_DAYS } = period;
  if (days < minDays) return `Minimum booking is ${minDays} day${minDays !== 1 ? 's' : ''}`;
  if (days > maxDays) return `Maximum booking is ${maxDays} day${maxDays !== 1 ? 's' : ''}`;
  return null;
};

/**
 * Calculate number of days between two dates.
 * @param {Date} startDate
 * @param {Date} endDate
 * @returns {number}
 */
export const getDayCount = (startDate, endDate) => {
  const msPerDay = 1000 * 60 * 60 * 24;
  return Math.round((endDate - startDate) / msPerDay);
};

/**
 * Validate period config.
 * @param {Object} period
 * @returns {string|null}
 */
export const validatePeriodConfig = (period) => {
  if (!period) return null;
  if (period.minDays !== undefined && period.minDays < 1) return 'Min days must be >= 1';
  if (period.maxDays !== undefined && period.maxDays < 1) return 'Max days must be >= 1';
  if (period.minDays && period.maxDays && period.minDays > period.maxDays) return 'Min must be <= max';
  return null;
};
